import { ReactElement, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import Button from '../controls/Button';
import { useData } from '../context/data.context';
import CustomDropdown from '../controls/CustomDropdown';
import { s3Service } from '../services/s3.service';
import { ingestDeviceData } from '../services/dashboard-service';

type InjestDeviceDataModalProps = {
  open: boolean;
  setOpen: (val: boolean) => void;
};

type FORMDATA = {
  device: string;
  file: FileList;
};

/**
 * Modal component for injesting device data.
 * @param props - Properties of the InjestDeviceDataModal component.
 * @returns - React Element.
 */
export default function InjestDeviceDataModal(props: InjestDeviceDataModalProps): ReactElement {
  const { open, setOpen } = props;
  const { devices: deviceList } = useData();
  const [loading, setLoading] = useState(false);

  const methods = useForm<FORMDATA>({
    mode: 'all'
  });

  const {
    handleSubmit,
    register,
    formState: { errors }
  } = methods;

  const deviceOptions = deviceList.map((device) => ({
    label: device.deviceName || device.serialNumber.toString(),
    value: device.serialNumber.toString()
  }));

  /**
   * Handles file upload to S3 and triggers data ingestion.
   * @param file - The file to upload.
   * @param deviceNumber - The device number to associate the data with.
   * @returns Promise that resolves when upload and ingestion are complete.
   */
  const handleFileUpload = async (file: File, deviceNumber: string): Promise<void> => {
    try {
      const key = `injest-${Date.now()}-${file.name}`;
      await s3Service.uploadFile(file, key);
      await ingestDeviceData(key, deviceNumber);
      toast.success('File uploaded successfully');
      setOpen(false);
    } catch (error) {
      toast.error('Error uploading file');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handles form submission and triggers file upload.
   * @param formData - Form data containing selected device and file information.
   * @returns Promise that resolves when upload process completes.
   */
  const onSubmit = async (formData: FORMDATA): Promise<void> => {
    if (formData.file?.[0]) {
      setLoading(true);
      await handleFileUpload(formData.file[0], formData.device);
    }
  };

  if (!open) return <div />;

  return (
    <div className="fixed flex inset-0 bg-background-600/90 z-[1000]">
      <div className="flex min-h-full flex-1 min-w-full items-center justify-center p-4 text-center sm:p-0">
        <div className="relative transform flex flex-1 rounded-lg max-w-4xl w-full bg-background-900 text-center shadow-xl transition-all p-2">
          <div className="px-4 pb-4 pt-5 w-full">
            <div className="sm:flex sm:items-center">
              <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                <h3 className="text-white text-sm font-medium">Upload Device Data</h3>
                {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                <FormProvider {...methods}>
                  <form noValidate action="post" onSubmit={handleSubmit(onSubmit)}>
                    <CustomDropdown
                      label="Device"
                      id="device"
                      options={deviceOptions}
                      validationObject={{ required: 'Please select a device' }}
                    />
                    <div className="mt-4">
                      <label
                        htmlFor="file"
                        className="block text-sm font-medium text-gray-500 mb-2"
                      >
                        Select File *
                        <input
                          type="file"
                          accept=".csv"
                          // eslint-disable-next-line react/jsx-props-no-spreading
                          {...register('file', {
                            required: 'Please upload a CSV file',
                            // eslint-disable-next-line jsdoc/require-jsdoc
                            validate: (files) => {
                              if (!files || files.length === 0) return 'Please upload a file';
                              const file = files[0];
                              if (file.type !== 'text/csv') return 'Please upload a CSV file';
                              return true;
                            }
                          })}
                          className={`w-full block py-1.5 px-3.5 text-base font-normal dark:text-gray-300 placeholder-gray-400 border rounded bg-gray-50 dark:bg-container-500 focus:dark:border-primary-300 focus-within:dark:ring-transparent mt-2 ${
                            errors.file ? 'border-error-300' : 'border-gray-700'
                          }`}
                        />
                      </label>
                      {errors.file && (
                        <p className="text-error-500 text-sm">{errors.file.message}</p>
                      )}
                    </div>
                    <div className="bg-background-900 flex gap-2 justify-between mt-8">
                      <div className="text-sm text-white/50">File format: CSV*</div>
                      <div className="flex gap-2">
                        <Button
                          theme="secondary-color"
                          size="sm"
                          name="Cancel"
                          onClick={() => setOpen(false)}
                        />
                        <Button
                          theme="primary"
                          loading={loading}
                          size="sm"
                          name="Upload"
                          buttonType="submit"
                        />
                      </div>
                    </div>
                  </form>
                </FormProvider>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
